"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { BRAND_NAME } from "@/lib/brand";
import { BrandWordmark } from "@/components/brand-wordmark";
import {
  PASSWORD_HINT,
  isPasswordPlausible,
} from "@/lib/password-policy";
import { createClient } from "@/lib/supabase/client";
import { purgeOfflineData } from "@/lib/offline-snapshot";
import { signInWithUsername } from "./actions";

/**
 * Login page — Sprint C2 + C2 overhaul.
 *
 * Three sign-in surfaces stacked:
 *   1. Continue with Google (primary OAuth button)
 *   2. Magic Link tab — email → "Send magic link" → check inbox
 *   3. Password tab — email + password (sign in OR sign up)
 *
 * The tab choice persists in localStorage so a return visitor lands
 * back on the method they used last time. Defaults to Magic Link
 * on first visit (it's the lightest-friction option).
 *
 * ## URL param: ?mode=signup
 *
 * The landing page's "Get started" CTA links here with
 * `?mode=signup`; new users land directly on the Password tab
 * with the form pre-set to signup (Confirm password input visible,
 * CTA reads "Create account"). This avoids the "tiny New here?
 * link buried below the form" discovery problem the C2 overhaul
 * fixed. `I already have an account` from the landing page omits
 * the param and lands on the default tab (last-used or Magic Link)
 * in sign-in mode.
 *
 * ## Layout philosophy (overhaul)
 *
 * Previous rev used `justify-center` which vertically centered the
 * brand block, eating most of the iPhone SE viewport before the
 * form was visible. New rev anchors content to the TOP with
 * comfortable top padding, leaving the form within thumb reach
 * without scroll. Standard SaaS pattern (Vercel, Linear, Stripe).
 *
 * ## Tabs (overhaul)
 *
 * Migrated from "pill-in-pill" treatment (active tab had its own
 * filled bg + border inside an outer pill) to the
 * underline-accent pattern used by the SegmentedPicker across
 * Settings + active-workout chrome. Single visual vocabulary for
 * "this is the selected option" across the app.
 *
 * No "Forgot password" affordance for now — magic link IS the
 * recovery path. A user without a remembered password can sign in
 * via magic link, then visit Settings → Account → Update password
 * to set a new one.
 */

type Tab = "magic" | "password";

/** The validated same-site return path from ?next (mirrors the open-redirect
 *  guard in /auth/callback), or null. Lets a share page route a brand-new user
 *  through signup and back to "/s/<token>?import=1" instead of stranding them
 *  on an empty dashboard with the workout they came for lost. */
function readReturnTo(): string | null {
  try {
    const raw = new URLSearchParams(window.location.search).get("next");
    return raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : null;
  } catch {
    return null;
  }
}

/** Auth-callback URL with ?next threaded, so the post-confirm / post-OAuth
 *  redirect lands back where the user started rather than the dashboard. */
function authCallbackUrl(origin: string): string {
  const next = readReturnTo();
  return next
    ? `${origin}/auth/callback?next=${encodeURIComponent(next)}`
    : `${origin}/auth/callback`;
}
type PasswordMode = "signin" | "signup";

const TAB_STORAGE_KEY = "outrepr.login.lastTab";
const MAGIC_LINK_RESEND_SECONDS = 30;
// Confirmation-email resends are a bit slower than magic links —
// the email arrives via the same SMTP pipeline but adds a "did
// they confirm yet?" delay before Supabase will accept another
// resend. 60s avoids both Supabase rate-limiting and user
// double-tap fatigue.
const CONFIRM_RESEND_SECONDS = 60;

export default function LoginPage() {
  const [tab, setTab] = useState<Tab>("magic");
  const [tabHydrated, setTabHydrated] = useState(false);
  // Password-form mode lives at the PAGE level so the H1
  // ("Sign in" vs "Create your account") and the top-of-form
  // new-user CTA stay in lockstep with the in-form mode. Previous
  // rev kept this state inside PasswordForm — toggling modes via
  // the bottom banner flipped the form's button labels but left
  // the page H1 stuck on the entry-mode value, which read as a
  // bug ("Create your account" header above a Sign in form).
  //
  // The state is initialized from the ?mode=signup URL param on
  // first mount (the "Get started" landing-page button arrives
  // with that param set). PasswordForm receives it as a
  // controlled prop with an onModeChange callback for the
  // bottom-banner toggle.
  const [passwordMode, setPasswordMode] = useState<PasswordMode>("signin");

  // Banner state — surfaced when the user arrives via the auth
  // callback. `?confirmed=1` is set on:
  //   - signup-confirmation links that landed here without a code
  //   - PKCE code-exchange failures (most often: clicked from a
  //     different browser than they signed up in)
  // In both cases Supabase has marked the user as confirmed; they
  // just need to sign in via password (or request a fresh magic
  // link if they don't have one set yet).
  const [confirmedBanner, setConfirmedBanner] = useState(false);
  // Shown after account deletion redirects here with ?deleted=1 — without it
  // the delete flow dropped the user on a bare login screen with no closure,
  // reading like a routine logout rather than a confirmed destructive action.
  const [deletedBanner, setDeletedBanner] = useState(false);

  // Hydrate the tab choice from localStorage + read ?mode=signup
  // off the URL. The two hydration steps share an effect so they
  // both happen on first client render. URL param wins over saved
  // tab — a user clicking "Get started" expects the signup
  // experience regardless of what they last used.
  useEffect(() => {
    let urlForcesSignup = false;
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("mode") === "signup") {
        urlForcesSignup = true;
        setPasswordMode("signup");
      }
      if (sp.get("confirmed") === "1") {
        setConfirmedBanner(true);
      }
      if (sp.get("deleted") === "1") {
        setDeletedBanner(true);
        // BACKSTOP for the offline purge that DeleteAccountRow runs before it
        // calls the action. That one can be interrupted - close the tab mid
        // redirect, or lose the process - and the leftover is a full snapshot
        // of a deleted account on a device with an unauthenticated page that
        // renders it.
        //
        // Keyed to `deleted=1` and nothing else. NOT to "no session": the app
        // layout redirects here whenever getAuthUser() comes back null, which
        // is ordinary session expiry, and purging on that would silently
        // destroy un-uploaded training belonging to an account that still
        // exists. This parameter is only ever set by the line after a
        // successful delete.
        void purgeOfflineData();
      }
    } catch {
      // URL parsing errors are vanishingly rare; ignore.
    }
    try {
      const saved = window.localStorage.getItem(TAB_STORAGE_KEY);
      if (urlForcesSignup) {
        // Signup is a Password-tab-only flow (magic link doesn't
        // distinguish signup from signin). Force Password tab
        // regardless of saved choice.
        setTab("password");
      } else if (saved === "magic" || saved === "password") {
        setTab(saved);
      }
    } catch {
      // localStorage can be blocked by privacy modes — fall back
      // to the default.
    }
    setTabHydrated(true);
  }, []);

  function selectTab(next: Tab) {
    setTab(next);
    try {
      window.localStorage.setItem(TAB_STORAGE_KEY, next);
    } catch {
      // No-op — see hydration comment above.
    }
  }

  return (
    // App-shell-style layout: brand row pinned top-left as a
    // mini nav header (acts as a "home" link), then the form
    // column centred below. Previously the brand was centred
    // above the H1, which made the two compete in the visual
    // hierarchy — small brand looked dominated by the H1 next to
    // it. Separating them into "nav chrome" + "page content"
    // tiers fixes the hierarchy: brand IS small (it's chrome,
    // not the hero) and the H1 IS large (it's the page title).
    // Standard pattern from Vercel / Linear / Notion / Stripe
    // dashboards.
    // pt/pb fold the iOS safe-area insets into the page's own padding
    // (calc keeps the design gap + adds the inset). /login is OUTSIDE the
    // (app) shell, so it doesn't inherit the shell header's
    // pt-[env(safe-area-inset-top)] — without this the brand row renders
    // under the status bar / Dynamic Island in a standalone PWA (the
    // status-bar style is black-translucent + viewport-fit=cover, so the
    // webview draws full-screen and we must inset content ourselves).
    <main className="flex flex-1 flex-col px-6 pt-[calc(1rem+env(safe-area-inset-top))] pb-[calc(3rem+env(safe-area-inset-bottom))]">
      {/* Top nav strip — brand row left-aligned, full px-6 width
          of the page (NOT constrained to max-w-sm) so it sits at
          the natural page edge rather than the form column's
          inset edge. <header> for correct landmark semantics. */}
      <header className="w-full">
        <BrandHeader />
      </header>

      {/* Centred form column — same max-w-sm container as before,
          sits in its own block below the nav strip. mt-3 (was
          mt-5; before that mt-8) tightens the gap between brand
          chrome and the H1 progressively as testing flagged the
          empty space. pt-4 on main + mt-3 here means brand →
          H1 is roughly 16+44+12 = 72px from the viewport top
          on iPhone SE — proper page header rhythm, no floating
          gap. */}
      <div className="w-full max-w-sm mx-auto mt-3 flex flex-col items-center gap-5">
        {/* H1 reflects LIVE mode (was tied to URL entry mode
            only). When the user toggles between signin/signup via
            the form's bottom banner, the H1 follows in real time —
            no more "Create your account" header above a Sign in
            form. text-2xl is the page's unambiguous hero size,
            uncontested by the small brand mark up in the nav. */}
        <h1 className="text-2xl font-semibold tracking-tight text-center">
          {/* The magic-link tab is sign-in ONLY (it can't distinguish signup,
              and rejects unknown emails), so never show "Create your account"
              over it — that contradiction greeted a new user arriving via "Get
              started" who tapped the Magic Link tab. On the Password tab the
              H1 follows the live signin/signup mode. */}
          {tab === "magic"
            ? "Sign in"
            : passwordMode === "signup"
              ? "Create your account"
              : "Sign in"}
        </h1>

        {/* Email-confirmed success banner. Shows when the auth/callback
            route redirected here with ?confirmed=1 (either no code
            arrived from Supabase, or the PKCE exchange failed because
            the verifier cookie isn't on this browser). The user IS
            confirmed at this point — they just need to sign in. We
            use the success token, not warn/danger, because nothing
            is broken from the user's perspective. Dismissible isn't
            necessary; the banner disappears the next time they hit
            the page without the query param. */}
        {confirmedBanner && (
          <div
            role="status"
            className="w-full rounded-md border border-brand px-3 py-2.5 text-sm text-fg"
          >
            <span className="font-medium text-brand">
              Email confirmed.
            </span>{" "}
            <span className="text-fg-muted">
              Sign in below to finish setting up your account.
            </span>
          </div>
        )}

        {/* Account-deleted confirmation. Mirrors the confirmed banner but
            acknowledges the destructive action so the user knows it worked. */}
        {deletedBanner && (
          <div
            role="status"
            className="w-full rounded-md border border-border px-3 py-2.5 text-sm text-fg-muted"
          >
            Your account and all its data have been deleted.
          </div>
        )}

        {/* Bidirectional mode-switch CTA. Replaces the previous
            signin-only "Create an account" link that routed to /
            (the landing page). Now it's the ONE mode-switch
            affordance on the page — the previous bottom in-form
            toggle inside PasswordForm has been dropped to avoid
            two competing call-outs that did almost the same thing.
            Clicking from the Magic Link tab also flips to the
            Password tab, since signup is password-only (magic-link
            signup isn't a Supabase primitive for our setup). */}
        <p className="w-full text-center text-sm text-fg-muted">
          {passwordMode === "signin" ? (
            <>
              New to <BrandWordmark />?{" "}
              <button
                type="button"
                onClick={() => {
                  setPasswordMode("signup");
                  if (tab !== "password") selectTab("password");
                }}
                className="text-brand hover:text-brand-strong font-semibold transition-colors"
              >
                Create an account
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  setPasswordMode("signin");
                  // Stay on the Password tab — user can choose to
                  // switch to Magic Link themselves if preferred.
                }}
                className="text-brand hover:text-brand-strong font-semibold transition-colors"
              >
                Sign in
              </button>
            </>
          )}
        </p>

        <ContinueWithGoogle />

        <div className="w-full flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-fg-dim text-[10px] uppercase tracking-wider">
            or
          </span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <SegmentedTabs
          active={tab}
          onSelect={selectTab}
          // Tabs disabled until hydration completes — keeps the
          // visual stable across the SSR→client transition.
          disabled={!tabHydrated}
        />

        {tab === "magic" ? (
          <MagicLinkForm />
        ) : (
          <PasswordForm
            // Controlled mode — the page owns the source of truth
            // so H1 + top CTA + form button label all stay in
            // sync. Mode changes happen via the page-level CTA
            // above the OAuth row; the in-form bottom toggle was
            // removed to avoid two competing controls.
            mode={passwordMode}
            // Forgot-password affordance in the Password form's
            // signin mode routes the user to the Magic Link tab.
            // Magic link IS our password-recovery path (see
            // module-level docstring) — there's no separate
            // /forgot-password page. Once the user signs in via
            // magic link they can set a new password in
            // Settings → Account.
            onForgotPassword={() => selectTab("magic")}
          />
        )}

        {/* Bottom "Back to home" link DROPPED — the top-left
            BrandHeader is itself a clickable Link to /, so the
            footer affordance was redundant chrome. Saves a line
            of vertical budget. */}
      </div>
    </main>
  );
}

// ──────────────────────────────────────────────────────────────────
// Brand + shared chrome
// ──────────────────────────────────────────────────────────────────

function BrandHeader() {
  // Horizontal brand row — logo + name on a single line, like a
  // mini app-shell header rather than the stacked hero treatment.
  // Login pages are utility chrome (the user's intent is "let me
  // in"), not a hero surface; the stacked logo + name stack we
  // were using bled landing-page conventions into the auth flow.
  // Standard SaaS auth pattern (Vercel / Linear / Stripe all
  // pin a compact brand mark up top and let the form be the
  // hero).
  //
  // Tagline ("One More Rep") dropped on /login entirely — it
  // lives on the landing page where it belongs (marketing
  // surface). Repeating it here read as marketing chrome in a
  // utility context.
  return (
    <Link
      href="/"
      className="opacity-90 hover:opacity-100 transition inline-flex items-center gap-2.5"
    >
      <Image
        // Transparent-bg variant so the brand glyph floats next
        // to the wordmark without a visible #000 tile around it.
        // `rounded-lg` kept as a harmless no-op — see the matching
        // notes in (app)/layout.tsx for the swap rationale.
        src="/repr-orange-no-bg.png"
        alt={BRAND_NAME}
        // 44px logo + text-xl name — bumped from 32px + text-lg.
        // Tester flagged the previous size as too small relative
        // to the H1 below. 44px is the iOS-native chrome-tier
        // (matches the app shell's tap-target floor) without
        // pushing into hero territory, and the matching text-xl
        // (20px) keeps logo + name visually balanced — neither
        // tier dominates the other.
        // The asset is 840×481 (~1.75:1) — height set to match that ratio
        // (44 × 481/840 ≈ 25) + style height:auto so Tailwind's preflight
        // `img { height: auto }` doesn't break the aspect ratio next/image
        // reserves (the "width or height modified" console warning).
        width={44}
        height={25}
        priority
        className="rounded-lg light:hidden"
        style={{ height: "auto" }}
      />
      {/* Light-theme logo (dark dumbbell), swapped via the `light:`
          variant against the pre-paint data-theme. */}
      <Image
        src="/repr-orange-no-bg-light.png"
        alt={BRAND_NAME}
        width={44}
        height={25}
        priority
        className="rounded-lg hidden light:block"
        style={{ height: "auto" }}
      />
      <span className="text-fg text-xl font-bold tracking-tight leading-none">
        <BrandWordmark />
      </span>
    </Link>
  );
}

function ContinueWithGoogle() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGoogle() {
    if (pending) return;
    setPending(true);
    setError(null);
    const supabase = createClient();
    const origin = window.location.origin;
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: authCallbackUrl(origin) },
    });
    if (oauthError) {
      setError(oauthError.message);
      setPending(false);
    }
    // On success, browser redirects to Google's OAuth flow.
  }

  return (
    <div className="w-full flex flex-col gap-2">
      <button
        type="button"
        onClick={handleGoogle}
        disabled={pending}
        className="w-full h-12 rounded-md bg-surface border border-border hover:border-fg-dim text-fg font-medium flex items-center justify-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <GoogleIcon />
        {pending ? "Redirecting…" : "Continue with Google"}
      </button>
      {error && (
        <p className="text-danger text-xs text-center" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Segmented tabs (Magic Link | Password)
// ──────────────────────────────────────────────────────────────────

function SegmentedTabs({
  active,
  onSelect,
  disabled,
}: {
  active: Tab;
  onSelect: (next: Tab) => void;
  disabled: boolean;
}) {
  // Overhaul: matches the SegmentedPicker active-state language
  // used across Settings + active-workout chrome — selected tab
  // gets `text-fg border-b-2 border-brand`, inactive stays muted
  // with a transparent reserve border. Single visual vocabulary
  // for "this option is selected" across every segmented control
  // in the app.
  return (
    <div
      role="tablist"
      className="w-full flex rounded-lg border border-border bg-surface-2 overflow-hidden text-sm"
    >
      <TabButton
        active={active === "magic"}
        onClick={() => onSelect("magic")}
        disabled={disabled}
        icon={<EnvelopeIcon />}
        label="Magic Link"
      />
      <TabButton
        active={active === "password"}
        onClick={() => onSelect("password")}
        disabled={disabled}
        icon={<KeyIcon />}
        label="Password"
      />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  disabled,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  disabled: boolean;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      disabled={disabled}
      className={
        // border-b-2 on both branches keeps the button height
        // identical between active / inactive states (only the
        // border colour changes). Same convention as
        // SegmentedPicker.
        "flex-1 h-10 inline-flex items-center justify-center gap-1.5 text-xs font-semibold transition-colors disabled:opacity-50 " +
        (active
          ? "text-fg border-b-2 border-brand"
          : "text-fg-dim hover:text-fg border-b-2 border-transparent")
      }
    >
      {icon}
      {label}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────
// Magic Link tab
// ──────────────────────────────────────────────────────────────────

function MagicLinkForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

  // Resend cooldown — tick down to 0 after a successful send so the
  // user can re-submit (e.g., if the first email got eaten by a
  // spam filter). Avoids hammering Supabase's rate limiter.
  useEffect(() => {
    if (status !== "sent" || resendSecondsLeft <= 0) return;
    const t = setTimeout(
      () => setResendSecondsLeft((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => clearTimeout(t);
  }, [status, resendSecondsLeft]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || status === "sending") return;
    if (status === "sent" && resendSecondsLeft > 0) return;

    setStatus("sending");
    setErrorMsg(null);

    const supabase = createClient();
    const origin = window.location.origin;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: authCallbackUrl(origin),
        // The magic-link tab is strictly a SIGN-IN surface — signup
        // is Password-tab-only (see the page-level mode-switch CTA;
        // magic-link signup isn't a flow we offer). Supabase's
        // default here is shouldCreateUser: TRUE, which silently
        // minted a brand-new empty account whenever a returning
        // user typo'd their email — they'd click the link, land on
        // an empty dashboard, and panic that their data vanished.
        // With false, Supabase rejects unknown emails and we map
        // the rejection to a friendly message below.
        shouldCreateUser: false,
      },
    });

    if (error) {
      setStatus("error");
      // Unknown-email rejection shape: GoTrue answers a
      // shouldCreateUser:false OTP request for a non-existent user
      // with "Signups not allowed for otp" (422, error code
      // `otp_disabled` on current supabase-js). Match BOTH the code
      // and (loosely) the message so an upstream wording tweak
      // doesn't regress the friendly copy back to raw API-speak.
      const isNoAccount =
        error.code === "otp_disabled" ||
        /signups?\s+not\s+allowed/i.test(error.message);
      setErrorMsg(
        isNoAccount
          ? "No account found with that email. Check the spelling, or create an account."
          : error.message,
      );
    } else {
      setStatus("sent");
      setResendSecondsLeft(MAGIC_LINK_RESEND_SECONDS);
    }
  }

  const canSubmit =
    email.length > 0 &&
    status !== "sending" &&
    !(status === "sent" && resendSecondsLeft > 0);

  return (
    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
      <p className="text-fg-dim text-xs">
        We&apos;ll email you a one-time link.
      </p>
      <label htmlFor="magic-email" className="sr-only">
        Email
      </label>
      <input
        id="magic-email"
        type="email"
        required
        autoComplete="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={status === "sending"}
        className="w-full h-12 px-4 rounded-md bg-surface border border-border focus:border-brand focus:outline-none text-fg placeholder:text-fg-dim transition-colors"
      />
      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full h-12 rounded-md bg-brand hover:bg-brand-strong text-on-brand font-semibold flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {status === "sending"
          ? "Sending…"
          : status === "sent" && resendSecondsLeft > 0
            ? `Resend in ${resendSecondsLeft}s`
            : status === "sent"
              ? "Resend magic link"
              : "Send magic link"}
      </button>

      {status === "sent" && (
        <p className="text-fg-muted text-sm text-center">
          We sent a sign-in link to{" "}
          <span className="text-fg font-medium">{email}</span>. Open the
          email and tap the link.
        </p>
      )}
      {errorMsg && (
        <p className="text-danger text-sm text-center" role="alert">
          {errorMsg}
        </p>
      )}
    </form>
  );
}

// ──────────────────────────────────────────────────────────────────
// Password tab (sign-in + sign-up)
// ──────────────────────────────────────────────────────────────────

function PasswordForm({
  mode,
  onForgotPassword,
}: {
  /** Controlled mode prop — page owns the source of truth so H1
   *  + top CTA stay in lockstep with the form. Mode changes happen
   *  via the page-level CTA above the OAuth row; this form just
   *  reacts to mode flips (and resets transient state via the
   *  useEffect on [mode] below). */
  mode: PasswordMode;
  /** Fired when the user taps "Forgot password?" in signin mode.
   *  The page-level handler routes them to the Magic Link tab —
   *  magic link IS our password-recovery path. */
  onForgotPassword?: () => void;
}) {
  // `identifier` is what the user typed — could be an email
  // (contains '@') or a username (doesn't). At signin time we
  // resolve usernames → emails via the server action that calls
  // the SECURITY DEFINER `resolve_email_by_username` RPC. Signup
  // is always email-only because Supabase Auth needs an email to
  // create the account; usernames are set later via the wizard
  // or Settings.
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pending, setPending] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [signUpSent, setSignUpSent] = useState(false);

  // "Email not confirmed" signin error gets its own dedicated state
  // so we can render an inline Resend button + cooldown alongside
  // the message, rather than a plain error string the user can do
  // nothing with. `unconfirmedEmail` doubles as the trigger and the
  // payload — non-null means "show the unconfirmed UI; this is the
  // email to resend confirmation to."
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(
    null,
  );

  // Resend-confirmation cooldown + status, mirroring MagicLinkForm's
  // resend pattern. Shared between the post-signup "check inbox"
  // screen and the "email not confirmed" signin-error path — both
  // surfaces call handleResendConfirmation.
  const [resendStatus, setResendStatus] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const [resendErrorMsg, setResendErrorMsg] = useState<string | null>(null);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

  // Resend cooldown tick — same shape as MagicLinkForm's effect.
  useEffect(() => {
    if (resendStatus !== "sent" || resendSecondsLeft <= 0) return;
    const t = setTimeout(
      () => setResendSecondsLeft((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => clearTimeout(t);
  }, [resendStatus, resendSecondsLeft]);

  function resetMessages() {
    setErrorMsg(null);
    setSignUpSent(false);
    setUnconfirmedEmail(null);
    setResendStatus("idle");
    setResendErrorMsg(null);
    setResendSecondsLeft(0);
  }

  /**
   * Resend the signup-confirmation email for `email`. Shared between:
   *   - the post-signup "Check your inbox" screen (Didn't arrive?)
   *   - the "email not confirmed" signin error path
   *
   * Uses Supabase's `auth.resend({ type: 'signup', ... })`. Same
   * `emailRedirectTo` we pass on the original signUp call so the
   * subsequent click lands back at our /auth/callback. Updates the
   * shared resend* state so the UI can show the cooldown / error /
   * success state regardless of which surface called it.
   */
  async function handleResendConfirmation(email: string) {
    if (resendStatus === "sending") return;
    if (resendStatus === "sent" && resendSecondsLeft > 0) return;

    setResendStatus("sending");
    setResendErrorMsg(null);

    const supabase = createClient();
    const origin = window.location.origin;
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: authCallbackUrl(origin) },
    });

    if (error) {
      setResendStatus("error");
      setResendErrorMsg(error.message);
    } else {
      setResendStatus("sent");
      setResendSecondsLeft(CONFIRM_RESEND_SECONDS);
    }
  }

  // Reset transient form state when the parent flips mode (via the
  // page-level CTA above the OAuth row). Without this, leftovers
  // from the previous mode bleed through: an unconfirmed-email
  // sub-screen captured during signin would still render in signup
  // mode, "wrong password" errors would persist across the toggle,
  // etc. Password + showPw are left alone — keeping the typed
  // email-and-password across a signin↔signup flip is desirable
  // UX (user realises mid-typing they want the other flow).
  useEffect(() => {
    resetMessages();
  }, [mode]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pending) return;
    resetMessages();

    setPending(true);
    const supabase = createClient();

    if (mode === "signin") {
      const resolvedEmail = identifier.trim();
      if (!resolvedEmail.includes("@")) {
        // Username path — fully server-side. signInWithUsername
        // resolves the handle AND performs the password sign-in on
        // the server, so the resolved email never reaches the
        // browser. The old flow here (resolve RPC → email back to
        // the client → client-side signInWithPassword) made the
        // resolve action an unauthenticated username → email
        // harvesting oracle; migration 0107 revoked the anon RPC
        // and this is the matching client half. Unknown username
        // and wrong password return the same generic message
        // (no enumeration via message differences), and the
        // unconfirmed-email state threads through to the same
        // Resend UI the email path uses.
        const result = await signInWithUsername(resolvedEmail, password);
        if (!result.ok) {
          if (result.unconfirmedEmail) {
            setUnconfirmedEmail(result.unconfirmedEmail);
          } else {
            setErrorMsg(result.error);
          }
          setPending(false);
          return;
        }
        // The action set the auth cookies; hard nav so the SSR
        // layer renders the signed-in state (same rationale as the
        // email path's window.location.assign below).
        window.location.assign(readReturnTo() ?? "/dashboard");
        return;
      }
      const { error } = await supabase.auth.signInWithPassword({
        email: resolvedEmail,
        password,
      });
      if (error) {
        // "Email not confirmed" is a distinct user-recoverable
        // state, not an authentication failure. Surface it with a
        // dedicated UI (inline Resend button) rather than the same
        // raw error string a wrong password produces. Supabase
        // returns `error_code: 'email_not_confirmed'` on newer
        // versions and the string `Email not confirmed` on older
        // ones; match both for robustness across version bumps.
        const isUnconfirmed =
          (error as { code?: string }).code === "email_not_confirmed" ||
          /email not confirmed/i.test(error.message);
        if (isUnconfirmed) {
          setUnconfirmedEmail(resolvedEmail);
        } else {
          setErrorMsg(error.message);
        }
      } else {
        // ─────────────────────────────────────────────────────
        // Browser password-manager save heuristic — known quirk
        // ─────────────────────────────────────────────────────
        //
        // Chrome / Edge / Firefox decide whether to prompt
        // "Save password for this site?" by watching for a form
        // submit → page navigation pair. Supabase's
        // signInWithPassword fires a fetch under the hood (not
        // a traditional form POST), and the navigation below is
        // a JS-driven `window.location.assign`, so the heuristic
        // sometimes misses the event entirely.
        //
        // Symptom: user successfully signs in, but Chrome never
        // offered to save the password, so on the next visit
        // there's nothing to autofill from the dropdown. Reads
        // as "the password manager is broken" — but it's a
        // browser-heuristic miss, not our bug.
        //
        // Worsened on localhost: Chrome's password manager
        // intentionally suppresses save prompts on `http://
        // localhost` for security reasons. The natural flow
        // works fine on production HTTPS (outrepr.com) for most
        // users. If users on PRODUCTION report no save / no
        // autofill, that's the signal to ship the
        // `PasswordCredential` API fallback below — it
        // explicitly hands the credential to the browser's
        // store, bypassing the heuristic entirely.
        //
        // ```ts
        // if (typeof window !== 'undefined' &&
        //     'credentials' in navigator &&
        //     'PasswordCredential' in window) {
        //   try {
        //     // @ts-ignore — PasswordCredential's TS types are
        //     // wonky; runtime contract is correct.
        //     const cred = new PasswordCredential({
        //       id: resolvedEmail,
        //       password,
        //       name: resolvedEmail,
        //     });
        //     await navigator.credentials.store(cred);
        //   } catch {
        //     // Browser doesn't support it / blocked by policy
        //     // — silent fallback to the heuristic.
        //   }
        // }
        // ```
        //
        // Magic-link users will NEVER see a save prompt because
        // there's no password to save — that's by design.
        //
        // On success Supabase sets cookies; navigate to the
        // authenticated landing surface. Hard nav to ensure the
        // SSR layer picks up the new session state.
        window.location.assign(readReturnTo() ?? "/dashboard");
        return;
      }
    } else {
      // Signup always email-only — Supabase Auth needs an email
      // to create the account. Username assignment happens later
      // via the wizard (sub-batch 5 step 4) or Settings.
      if (!identifier.includes("@")) {
        setErrorMsg("Please enter your email address to sign up.");
        setPending(false);
        return;
      }
      const origin = window.location.origin;
      const { error } = await supabase.auth.signUp({
        email: identifier.trim(),
        password,
        options: { emailRedirectTo: authCallbackUrl(origin) },
      });
      if (error) {
        // Detect the "email already registered" case. Supabase's
        // exact message varies by version ("User already registered",
        // "user with this email already exists") so match a few
        // common phrasings rather than depending on a single
        // string. Friendlier message + nudge the user toward
        // signin instead of leaving them stuck.
        const alreadyRegistered =
          (error as { code?: string }).code === "user_already_exists" ||
          /already (registered|exists)/i.test(error.message);
        if (alreadyRegistered) {
          setErrorMsg(
            "That email's already registered. Switch to Sign in below - or use Forgot password via magic link if you've forgotten it.",
          );
        } else {
          setErrorMsg(error.message);
        }
      } else {
        setSignUpSent(true);
      }
    }

    setPending(false);
  }

  // Resend availability — both surfaces below use the same gate.
  // Defined inside the component so we read the live state.
  const canResend =
    resendStatus !== "sending" &&
    !(resendStatus === "sent" && resendSecondsLeft > 0);

  // Button label for the resend action across both surfaces. The
  // disabled-with-countdown state ("Resend in 45s") doubles as the
  // status display — keeps the inline row compact instead of pairing
  // a separate countdown line with the button.
  const resendButtonLabel =
    resendStatus === "sending"
      ? "Sending…"
      : resendStatus === "sent" && resendSecondsLeft > 0
        ? `Resend in ${resendSecondsLeft}s`
        : "Resend";

  // Lead-in text for the inline pair. After a successful send the
  // text flips to "Sent — check your inbox." so the user gets clear
  // feedback without an extra stacked line.
  const resendLeadIn =
    resendStatus === "sent" ? "Sent - check your inbox." : "Didn't get it?";

  if (signUpSent) {
    return (
      <div className="w-full flex flex-col gap-4 text-center">
        <p className="text-fg-muted text-sm">
          Check your inbox at{" "}
          <span className="text-fg font-medium">{identifier}</span> -
          tap the confirmation link to finish creating your account.
        </p>

        {/* Inline pair (Option A). Single row of secondary recovery
            actions: lead-in text + Resend action + bullet · + Use a
            different email action. Replaces the previous stacked
            "Didn't arrive? / Resend / Use a different email" cluster
            that read as three orphaned affordances. Both verbs use
            brand-orange at the same weight — neither is "primary"
            (the primary action is the implicit "go check your
            inbox" instruction above). flex-wrap keeps it readable
            on narrow viewports if the row needs to break. */}
        <div className="flex flex-col items-center gap-1 text-xs">
          <p
            className="flex items-center justify-center gap-2 flex-wrap"
            aria-live="polite"
          >
            <span
              className={
                resendStatus === "sent" ? "text-fg-muted" : "text-fg-dim"
              }
            >
              {resendLeadIn}
            </span>
            <button
              type="button"
              onClick={() => handleResendConfirmation(identifier.trim())}
              disabled={!canResend}
              className="text-brand hover:text-brand-strong font-medium transition disabled:text-fg-dim disabled:cursor-not-allowed tabular-nums"
            >
              {resendButtonLabel}
            </button>
            <span aria-hidden className="text-fg-dim">
              ·
            </span>
            <button
              type="button"
              onClick={() => {
                setSignUpSent(false);
                setPassword("");
                // Reset the resend cooldown — different email gets
                // a fresh attempt window.
                setResendStatus("idle");
                setResendErrorMsg(null);
                setResendSecondsLeft(0);
              }}
              className="text-brand hover:text-brand-strong font-medium transition"
            >
              Use a different email
            </button>
          </p>
          {resendErrorMsg && (
            <p className="text-danger" role="alert">
              {resendErrorMsg}
            </p>
          )}
        </div>
      </div>
    );
  }

  // "Email not confirmed" signin-error screen. Same shape as the
  // post-signup confirm screen but routed via the signin path —
  // shows the user's typed email, and a back-link that lets them
  // re-enter the signin form to try a different account.
  if (unconfirmedEmail) {
    return (
      <div className="w-full flex flex-col gap-4 text-center">
        <p className="text-fg-muted text-sm">
          Your account at{" "}
          <span className="text-fg font-medium">{unconfirmedEmail}</span>{" "}
          exists but the email hasn&apos;t been confirmed yet. Tap the
          link in your inbox to finish, or resend it below.
        </p>

        <div className="flex flex-col items-center gap-1 text-xs">
          <p
            className="flex items-center justify-center gap-2 flex-wrap"
            aria-live="polite"
          >
            <span
              className={
                resendStatus === "sent" ? "text-fg-muted" : "text-fg-dim"
              }
            >
              {resendLeadIn}
            </span>
            <button
              type="button"
              onClick={() => handleResendConfirmation(unconfirmedEmail)}
              disabled={!canResend}
              className="text-brand hover:text-brand-strong font-medium transition disabled:text-fg-dim disabled:cursor-not-allowed tabular-nums"
            >
              {resendButtonLabel}
            </button>
            <span aria-hidden className="text-fg-dim">
              ·
            </span>
            <button
              type="button"
              onClick={() => {
                setUnconfirmedEmail(null);
                setResendStatus("idle");
                setResendErrorMsg(null);
                setResendSecondsLeft(0);
              }}
              className="text-brand hover:text-brand-strong font-medium transition"
            >
              Back to sign in
            </button>
          </p>
          {resendErrorMsg && (
            <p className="text-danger" role="alert">
              {resendErrorMsg}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Plausibility gate ONLY — Supabase enforces min length /
  // complexity / breach-check server-side per the Auth → Policies
  // dashboard. Submitting a weak password surfaces Supabase's
  // error message through the existing errorMsg channel.
  const canSubmit =
    identifier.length > 0 && isPasswordPlausible(password) && !pending;

  return (
    // `noValidate` disables the browser's native HTML5 input
    // validation (`type="email"` would otherwise reject a username
    // input because it lacks an "@"). Our JS handles validation
    // + email/username routing at submit time, so the native
    // check is just friction.
    //
    // Why `type="email"` even in signin mode: Chrome's
    // password-manager heuristic reads <input type="text"> +
    // <input type="password"> together as "potential signup form"
    // and overrides our `autoComplete="current-password"` hint —
    // triggering the strong-password generator on a SIGN-IN
    // surface. Standard fix: use `type="email"` for the username
    // field (the canonical Chrome sign-in shape) + noValidate so
    // we still accept usernames.
    <form
      noValidate
      onSubmit={handleSubmit}
      className="w-full flex flex-col gap-3"
    >
      {/* Form-intro line dropped — H1 + tabs + input fields +
          button label already communicate the flow. Kept on the
          Magic Link form because it adds genuine info ("we'll
          email you a link") that the inputs alone don't surface.
          Identifier field accepts BOTH email and username in
          signin mode (sub-batch 5 — usernames sign in via the
          signInWithUsername server action, which resolves the
          handle and authenticates entirely server-side).
          Signup mode still requires email since Supabase Auth
          needs an email to create the account. */}
      <label htmlFor="pw-identifier" className="sr-only">
        {mode === "signin" ? "Email or username" : "Email"}
      </label>
      <input
        id="pw-identifier"
        // type=email always — needed for Chrome's password-
        // manager heuristic to recognise this as a sign-in form
        // (see the noValidate comment on the form element above).
        // Usernames pass through fine because the form has
        // noValidate; we route them via signInWithUsername
        // at submit time.
        type="email"
        required
        autoComplete={mode === "signin" ? "username" : "email"}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder={
          mode === "signin"
            ? "you@example.com or username"
            : "you@example.com"
        }
        value={identifier}
        onChange={(e) => setIdentifier(e.target.value)}
        disabled={pending}
        className="w-full h-12 px-4 rounded-md bg-surface border border-border focus:border-brand focus:outline-none text-fg placeholder:text-fg-dim transition-colors"
      />

      <div className="relative">
        <label htmlFor="pw-password" className="sr-only">
          Password
        </label>
        <input
          id="pw-password"
          type={showPw ? "text" : "password"}
          required
          autoComplete={
            mode === "signin" ? "current-password" : "new-password"
          }
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={pending}
          className="w-full h-12 px-4 pr-14 rounded-md bg-surface border border-border focus:border-brand focus:outline-none text-fg placeholder:text-fg-dim transition-colors"
        />
        <button
          type="button"
          onClick={() => setShowPw((v) => !v)}
          tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-7 px-2 text-fg-dim hover:text-fg text-xs"
        >
          {showPw ? "Hide" : "Show"}
        </button>
      </div>

      {/* Signin-mode "Forgot password?" affordance. Routes to
          the Magic Link tab via the page-level callback — magic
          link IS our recovery path (see module docstring). Small
          right-aligned text link so it doesn't fight the primary
          Sign in CTA for attention. */}
      {mode === "signin" && onForgotPassword && (
        <div className="flex justify-end -mt-1">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-fg-muted hover:text-brand text-xs transition-colors"
          >
            Forgot password?
          </button>
        </div>
      )}

      {/* Signup-only password hint. Single source of truth in
          src/lib/password-policy.ts so this and every other
          password input share one copy. KEEP IN SYNC with the
          Supabase Auth → Policies dashboard. */}
      {mode === "signup" && (
        <p className="text-fg-dim text-xs -mt-1">{PASSWORD_HINT}</p>
      )}

      {/* Confirm-password field DROPPED. The Show/Hide toggle on
          the primary input gives users a way to visually verify
          their typed password before submit — that's the modern
          alternative to "type it twice." Stripe / GitHub / Linear
          / Notion all dropped confirm fields years ago. Net win:
          one less input on signup, ~60px vertical reclaim,
          friction reduction for users who didn't fat-finger
          (the majority). */}

      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full h-12 rounded-md bg-brand hover:bg-brand-strong text-on-brand font-semibold flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {pending
          ? mode === "signin"
            ? "Signing in…"
            : "Creating account…"
          : mode === "signin"
            ? "Sign in"
            : "Create account"}
      </button>

      {/* Signup-only Terms + Privacy microcopy. Sits BELOW the
          Create account button (industry convention — submit
          implies agreement) rather than above it. Tightened from
          15 words ("By creating an account you agree to OutRepR's
          Terms of Service and Privacy Policy.") down to 10 words
          so it usually wraps to one line on iPhone SE — the
          longer version was wrapping to three lines and reading
          as busy chrome under the Create account button. Stripe /
          Linear / Vercel / Discord all use this tightened pattern
          rather than spelling out "of Service" / "of OutRepR." */}
      {mode === "signup" && (
        <p className="text-fg-dim text-xs text-center leading-relaxed">
          By signing up you agree to our{" "}
          <Link
            href="/terms"
            className="text-fg-muted hover:text-fg underline underline-offset-2 transition-colors"
          >
            Terms
          </Link>{" "}
          &{" "}
          <Link
            href="/privacy"
            className="text-fg-muted hover:text-fg underline underline-offset-2 transition-colors"
          >
            Privacy Policy
          </Link>
          .
        </p>
      )}

      {errorMsg && (
        <p className="text-danger text-sm text-center" role="alert">
          {errorMsg}
        </p>
      )}

      {/* In-form mode toggle removed — the page-level CTA above the
          OAuth row is now the single source of truth for switching
          between signin and signup. Two competing toggles read as
          duplicated chrome and gave users a confusing fork. The
          page-level CTA stays visible above the form regardless of
          which tab is active, so mode-switching is still one click
          away from any state. */}
    </form>
  );
}

// ──────────────────────────────────────────────────────────────────
// Icons
// ──────────────────────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92A8.77 8.77 0 0 0 17.64 9.2Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.92v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.19.29-1.72V4.95H.92A8.99 8.99 0 0 0 0 9c0 1.45.35 2.82.92 4.05l3.05-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .92 4.95l3.05 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

function EnvelopeIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <polyline points="3 7 12 13 21 7" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  );
}
